import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeIndianRupee,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  KeyRound,
  Phone,
  RefreshCw,
  Search,
  Store,
  Truck,
  UserRound,
  X,
} from "lucide-react";
import { restaurantAdminService, rowsOf, unwrap } from "../../services/restaurantAdmin.service";
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Input,
  LoadingBox,
  PageHeader,
  Select,
  StatusPill,
  TableShell,
  formatCurrency,
  idOf,
} from "./components/FoodAdminUi";

/* ─────────────────────────────────────────────────────────────────────────────
   Utility helpers — unchanged
───────────────────────────────────────────────────────────────────────────── */

function first(...values) {
  return values.find((v) => v !== undefined && v !== null && v !== "");
}

function orderNumber(row = {}) {
  return first(row.orderNumber, row.order_number, row.orderNo, row.order_no, row.id, "-");
}

function total(row = {}) {
  return first(row.grandTotal, row.grand_total, row.totalAmount, row.total_amount, row.payableAmount, row.payable_amount, 0);
}

function itemsOf(order = {}) {
  return Array.isArray(order.items) ? order.items : [];
}

function itemSnapshot(item = {}) {
  return item.snapshot || item.itemSnapshot || item.item_snapshot || {};
}

function itemName(item = {}) {
  const snap = itemSnapshot(item);
  return first(item.name, item.itemName, item.foodName, snap.variantName, snap.variant_name, snap.name, snap.itemName, snap.foodName, "Item");
}

function parentItemName(item = {}) {
  const snap = itemSnapshot(item);
  return first(snap.parentName, snap.parent_name, snap.foodName, snap.food_name, snap.itemName, snap.item_name, "");
}

function itemQty(item = {}) {
  return Number(first(item.qty, item.quantity, 1));
}

function itemUnitPrice(item = {}) {
  return Number(first(item.unitPrice, item.unit_price, item.price, itemSnapshot(item).unitPrice, 0));
}

function itemLineTotal(item = {}) {
  return Number(first(item.lineTotal, item.line_total, item.total, 0)) || itemUnitPrice(item) * itemQty(item);
}

function addressText(address = {}, fallback = "") {
  if (typeof address === "string") return address;
  return first(
    fallback,
    address.fullAddress,
    address.full_address,
    [
      address.addressLine1 || address.address_line1 || address.line1,
      address.addressLine2 || address.address_line2 || address.line2,
      address.landmark,
      address.city,
      address.state,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", "),
    "-"
  );
}

function driverTask(order = {}) {
  return order.driverTask || order.driver_task || null;
}

function driverStatus(order = {}) {
  const task = driverTask(order) || {};
  return first(
    order.driverStatus,
    order.driver_status,
    task.taskStatus,
    task.task_status,
    task.assignmentStatus,
    task.assignment_status,
    "NOT_ASSIGNED"
  );
}

function financeOf(order = {}) {
  return order.financials || order.finance || {};
}

function isPayOnDeliveryFoodOrder(order = {}) {
  const method = String(first(
    order.paymentMethod,
    order.payment_method,
    order.paymentMode,
    order.payment_mode,
    order.payOnDelivery?.method,
    order.pay_on_delivery?.method,
    order.pricing?.payOnDelivery?.method,
    order.pricing?.pay_on_delivery?.method,
    ''
  )).trim().toUpperCase();
  return ['PAY_ON_DELIVERY', 'POD', 'COD', 'RAZORPAY_POD', 'PAY_ON_DELIVERY_RAZORPAY'].includes(method);
}

function moneyValue(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function deepCandidates(order = {}) {
  const task = driverTask(order) || {};
  const pricing = order.pricing || {};
  const meta = task.metadata || task.deliveryTaskMetadata || task.delivery_task_metadata || {};
  return [
    order.driver,
    order.driverSnapshot,
    order.driver_snapshot,
    task.driver,
    task.partner,
    task.assignedPartner,
    task.assigned_partner,
    meta.assignedPartner,
    meta.assigned_partner,
    pricing.dispatch?.assignedPartner,
    pricing.dispatch?.assigned_partner,
    pricing.driverReservation?.assignedPartner,
    pricing.driverReservation?.assigned_partner,
    pricing.reservation?.assignedPartner,
    pricing.reservation?.assigned_partner,
    pricing.serviceabilitySnapshot?.assignedPartner,
    pricing.serviceabilitySnapshot?.assigned_partner,
  ].filter(Boolean);
}

function driverInfo(order = {}) {
  const task = driverTask(order) || {};
  const meta = task.metadata || {};
  const candidates = deepCandidates(order);
  const readFrom = (keys = []) => {
    for (const source of candidates) {
      for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== "") return value;
      }
    }
    return undefined;
  };

  const id = first(
    task.partnerId,
    task.partner_id,
    task.driverId,
    order.driverPartnerId,
    order.driver_partner_id,
    readFrom(["id", "partnerId", "partner_id", "driverId", "driver_id"]),
    ""
  );

  const name = first(
    order.driverName,
    order.driver_name,
    task.name,
    task.driverName,
    task.driver_name,
    meta.partnerName,
    meta.partner_name,
    meta.driverName,
    meta.driver_name,
    readFrom(["name", "fullName", "full_name", "driverName", "driver_name", "partnerName", "partner_name"]),
    ""
  );

  const phone = first(
    order.driverPhone,
    order.driver_phone,
    task.phone,
    task.driverPhone,
    task.driver_phone,
    meta.partnerPhone,
    meta.partner_phone,
    meta.driverPhone,
    meta.driver_phone,
    readFrom(["phone", "mobile", "phoneNumber", "phone_number", "driverPhone", "driver_phone", "partnerPhone", "partner_phone"]),
    ""
  );

  return {
    id,
    name,
    displayName: name || (id ? "Assigned driver" : "Not assigned"),
    phone,
    status: driverStatus(order),
    assignment: first(task.assignmentStatus, task.assignment_status, "NOT_ASSIGNED"),
    taskStatus: first(task.taskStatus, task.task_status, "-"),
    autoAssigned: task.autoAssigned ?? task.auto_assigned ?? false,
    lastNote: first(task.lastStatusNote, task.last_status_note, ""),
    receivedAt: first(task.receivedAt, task.received_at, ""),
    pickedUpAt: first(task.pickedUpAt, task.picked_up_at, ""),
    outForDeliveryAt: first(task.outForDeliveryAt, task.out_for_delivery_at, ""),
    deliveredAt: first(task.deliveredAt, task.delivered_at, ""),
  };
}

function needsManualAssignment(order = {}) {
  const status = String(driverStatus(order) || "").toUpperCase();
  const orderStatus = String(order.status || "").toUpperCase();
  return (
    status.includes("MANUAL") ||
    status.includes("NO_RIDER") ||
    orderStatus.includes("MANUAL_ASSIGNMENT") ||
    orderStatus.includes("NO_RIDER")
  );
}

function assignmentDiagnostics(order = {}) {
  const task = driverTask(order) || {};
  const meta = task.metadata || task.deliveryTaskMetadata || task.delivery_task_metadata || {};
  return meta.assignmentDiagnostics || meta.assignment_diagnostics || null;
}

function timeText(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

function dateText(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-IN");
}

function shortText(value, size = 8) {
  const str = String(value || "");
  if (!str) return "-";
  return str.length > size ? `${str.slice(0, size)}…` : str;
}

function otpValue(order = {}, type = "pickup") {
  const task = driverTask(order) || {};
  const verification = order.verification || {};
  if (type === "delivery") {
    return first(
      order.deliveryOtp, order.delivery_otp,
      verification.deliveryOtp, verification.delivery_otp,
      task.deliveryOtp, task.delivery_otp, ""
    );
  }
  return first(
    order.pickupOtp, order.pickup_otp,
    verification.pickupOtp, verification.pickup_otp,
    task.pickupOtp, task.pickup_otp, ""
  );
}

function otpVerifiedAt(order = {}, type = "pickup") {
  const task = driverTask(order) || {};
  if (type === "delivery") {
    return first(
      order.deliveryOtpVerifiedAt, order.delivery_otp_verified_at,
      task.deliveryOtpVerifiedAt, task.delivery_otp_verified_at,
      order.deliveredAt, order.delivered_at, task.deliveredAt, task.delivered_at, ""
    );
  }
  return first(
    order.pickupOtpVerifiedAt, order.pickup_otp_verified_at,
    task.pickupOtpVerifiedAt, task.pickup_otp_verified_at,
    order.pickedUpAt, order.picked_up_at, task.pickedUpAt, task.picked_up_at, ""
  );
}

function otpVerified(order = {}, type = "pickup") {
  const status = String(first(order.status, order.orderStatus, order.order_status, "")).toUpperCase();
  if (type === "delivery") return Boolean(otpVerifiedAt(order, type)) || status === "DELIVERED";
  return (
    Boolean(otpVerifiedAt(order, type)) ||
    ["PICKED_UP", "OUT_FOR_DELIVERY", "DELIVERED"].includes(status)
  );
}

function pricingOf(order = {}) {
  return order.pricing || order.pricingSnapshot || order.pricing_snapshot || {};
}

function objectOrEmpty(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function cancellationOf(order = {}) {
  const pricing = pricingOf(order);
  return objectOrEmpty(order.cancellation || order.cancel || pricing.cancellation || pricing.cancel || {});
}

function failureOf(order = {}) {
  const pricing = pricingOf(order);
  return objectOrEmpty(
    order.paymentFailure || order.payment_failure || order.failure ||
    order.orderFailure || order.order_failure ||
    pricing.paymentFailure || pricing.payment_failure || pricing.failure ||
    pricing.orderFailure || pricing.order_failure || {}
  );
}

function issueOf(order = {}) {
  const c = cancellationOf(order);
  const f = failureOf(order);
  if (Object.keys(c).length) return c;
  if (Object.keys(f).length) return f;

  const orderStatus = String(first(order.status, order.orderStatus, order.order_status, "")).toUpperCase();
  const paymentStatus = String(first(order.paymentStatus, order.payment_status, "")).toUpperCase();
  if (paymentStatus === "FAILED") {
    const noGatewayOrder = !first(
      order.razorpayOrderId, order.razorpay_order_id,
      order.razorpayPaymentId, order.razorpay_payment_id, ""
    );
    return {
      failed: true,
      reason: noGatewayOrder
        ? "Delivery partner was not available, so checkout was cancelled before payment"
        : "Food payment failed/cancelled before completion",
      source: noGatewayOrder ? "DRIVER_RESERVATION" : "PAYMENT_FAILED",
      code: noGatewayOrder ? "FOOD_DRIVER_NOT_AVAILABLE" : "FOOD_PAYMENT_FAILED",
    };
  }
  if (orderStatus === "CANCELLED") {
    return {
      cancelled: true,
      reason: "Food order was cancelled",
      source: "ORDER_CANCELLED",
      code: "FOOD_ORDER_CANCELLED",
    };
  }
  return {};
}

function refundOf(order = {}) {
  const c = cancellationOf(order);
  const direct = order.refund || order.refundInfo || order.refund_info || null;
  const refund = direct || c.refund || c.razorpayRefund || c.razorpay_refund || {};
  return refund && typeof refund === "object" ? refund : {};
}

function hasRefundInfo(order = {}) {
  const refund = refundOf(order);
  const paymentStatus = String(first(order.paymentStatus, order.payment_status, "")).toUpperCase();
  return (
    paymentStatus === "REFUNDED" ||
    Boolean(first(refund.id, refund.refundId, refund.refund_id, refund.raw?.id, ""))
  );
}

function cancelReasonOf(order = {}) {
  const issue = issueOf(order);
  return first(
    order.cancelReason, order.cancel_reason,
    order.cancellationReason, order.cancellation_reason,
    order.failureReason, order.failure_reason,
    issue.reason, issue.cancelReason, issue.cancel_reason,
    issue.failureReason, issue.failure_reason, issue.message,
    issue.gatewayPaymentStatus, issue.gateway_payment_status,
    "-"
  );
}

function issueSourceOf(order = {}) {
  const issue = issueOf(order);
  return first(issue.source, issue.code, issue.type, "-");
}

function isCancelledOrRefunded(order = {}) {
  const orderStatus = String(first(order.status, order.orderStatus, order.order_status, "")).toUpperCase();
  const paymentStatus = String(first(order.paymentStatus, order.payment_status, "")).toUpperCase();
  return orderStatus === "CANCELLED" || paymentStatus === "REFUNDED" || paymentStatus === "FAILED";
}

function refundAmountRupees(refund = {}, fallback = 0) {
  const rupees = moneyValue(refund.amountRupees, refund.amount_rupees);
  if (rupees > 0) return rupees;
  const paise = moneyValue(
    refund.amount, refund.requestedAmount, refund.requested_amount,
    refund.refundedThisAttempt, refund.refunded_this_attempt
  );
  if (paise > 0) return paise / 100;
  return moneyValue(fallback);
}

function refundCreatedAt(refund = {}) {
  const raw = first(refund.createdAt, refund.created_at, refund.raw?.created_at, "");
  if (!raw) return "-";
  if (typeof raw === "number") return timeText(raw * 1000);
  return timeText(raw);
}

function canCancelFoodOrder(order = {}) {
  const status = String(first(order.status, order.orderStatus, order.order_status, "")).toUpperCase();
  const paymentStatus = String(first(order.paymentStatus, order.payment_status, "")).toUpperCase();
  const dStatus = String(driverStatus(order || "")).toUpperCase();
  const d = driverInfo(order || {});
  const isPodPending = isPayOnDeliveryFoodOrder(order) && paymentStatus === "PAYMENT_PENDING_ON_DELIVERY";

  if (["CANCELLED", "DELIVERED"].includes(status) || ["CANCELLED", "DELIVERED"].includes(dStatus)) {
    return false;
  }

  if (isPodPending) {
    return true;
  }

  return (
    paymentStatus === "PAID" &&
    !["PICKED_UP", "OUT_FOR_DELIVERY"].includes(dStatus) &&
    !d.pickedUpAt
  );
}


function canManualAssignFoodOrder(order = {}) {
  const status = String(first(order.status, order.orderStatus, order.order_status, "")).toUpperCase();
  const paymentStatus = String(first(order.paymentStatus, order.payment_status, "")).toUpperCase();
  const dStatus = String(driverStatus(order || "")).toUpperCase();
  const d = driverInfo(order || {});
  const notPickedYet = !["PICKED_UP", "OUT_FOR_DELIVERY", "DELIVERED"].includes(dStatus) && !d.pickedUpAt;
  const driverMissingOrManual = !d.id || needsManualAssignment(order) || ["NOT_ASSIGNED", "PENDING_ASSIGNMENT"].includes(String(d.assignment || d.status || "").toUpperCase());
  return (
    paymentStatus === "PAID" &&
    !["CANCELLED", "DELIVERED"].includes(status) &&
    notPickedYet &&
    driverMissingOrManual
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Detail helpers (unchanged — used by OrderDetailModal)
───────────────────────────────────────────────────────────────────────────── */

function DetailBlock({ title, icon, children }) {
  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase text-slate-800">
        {icon || null}
        {title}
      </div>
      {children}
    </div>
  );
}

function MoneyTile({ label, value }) {
  return (
    <div className="rounded-[16px] border border-[#f2c6cc] p-4 text-slate-800">
      <div className="text-[10px] font-extrabold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-lg font-semibold">{formatCurrency(value)}</div>
    </div>
  );
}

function OtpTile({ label, value, verified, verifiedAt, helper }) {
  return (
    <div className="rounded-[16px] border border-[#f2c6cc] bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wide text-slate-500">
          <KeyRound size={14} className="text-[#d86a78]" /> {label}
        </div>
        <StatusPill value={verified ? "VERIFIED" : "PENDING"} />
      </div>
      <div className="mt-2 font-mono text-2xl font-black tracking-[0.28em] text-black">{value || "----"}</div>
      <div className="mt-2 text-xs font-semibold leading-5 text-slate-500">{helper}</div>
      {verifiedAt ? (
        <div className="mt-2 text-xs font-bold text-emerald-600">Verified: {timeText(verifiedAt)}</div>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   NEW: Redesigned status badge for list view
───────────────────────────────────────────────────────────────────────────── */

const STATUS_MAP = {
  DELIVERED:          { bg: "#ecfdf5", fg: "#065f46", dot: "#10b981" },
  OUT_FOR_DELIVERY:   { bg: "#eff6ff", fg: "#1e40af", dot: "#3b82f6" },
  PICKED_UP:          { bg: "#dbeafe", fg: "#1e40af", dot: "#2563eb" },
  ASSIGNED:           { bg: "#f0fdf4", fg: "#166534", dot: "#22c55e" },
  DELIVERY_ASSIGNED:  { bg: "#f0fdf4", fg: "#166534", dot: "#22c55e" },
  AUTO_ACCEPTED:      { bg: "#fefce8", fg: "#854d0e", dot: "#eab308" },
  PAYMENT_PENDING:    { bg: "#f4f4f5", fg: "#52525b", dot: "#a1a1aa" },
  PICKUP_PENDING:     { bg: "#fff7ed", fg: "#9a3412", dot: "#f97316" },
  CANCELLED:          { bg: "#fef2f2", fg: "#991b1b", dot: "#ef4444" },
  PAID:               { bg: "#f0fdf4", fg: "#166534", dot: "#22c55e" },
  FAILED:             { bg: "#fef2f2", fg: "#991b1b", dot: "#ef4444" },
  REFUNDED:           { bg: "#fdf4ff", fg: "#6b21a8", dot: "#a855f7" },
  NOT_ASSIGNED:       { bg: "#f4f4f5", fg: "#71717a", dot: "#d4d4d8" },
  MANUAL_ASSIGNMENT:  { bg: "#fffbeb", fg: "#92400e", dot: "#f59e0b" },
  VERIFIED:           { bg: "#f0fdf4", fg: "#166534", dot: "#22c55e" },
  PENDING:            { bg: "#fafafa", fg: "#71717a", dot: "#d4d4d8" },
};

function Badge({ value }) {
  if (!value || value === "-") return <span className="text-zinc-300 text-xs">—</span>;
  const key = String(value).toUpperCase();
  const cfg = STATUS_MAP[key] || { bg: "#f4f4f5", fg: "#52525b", dot: "#a1a1aa" };
  const label = key.replace(/_/g, " ");
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: cfg.bg, color: cfg.fg,
        fontSize: 11, fontWeight: 600, letterSpacing: "0.02em",
        borderRadius: 6, padding: "3px 8px", whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   NEW: Redesigned pagination
───────────────────────────────────────────────────────────────────────────── */

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function PaginationBar({ page, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange, itemLabel = "items" }) {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page || 1), safeTotalPages);
  const startItem = Math.min((safePage - 1) * pageSize + 1, totalItems);
  const endItem = Math.min(safePage * pageSize, totalItems);

  if (!totalItems) return null;

  return (
    <div className="flex flex-col items-start gap-3 px-5 py-3 border-t border-zinc-100 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-zinc-500 tabular-nums">
        {startItem}–{endItem} of {totalItems} {itemLabel}
      </p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 mr-1">Rows</span>
        <select
          value={pageSize}
          onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
          className="h-8 rounded-lg border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-700 outline-none focus:ring-2 focus:ring-zinc-200 cursor-pointer"
        >
          {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, safePage - 1))}
            disabled={safePage <= 1}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="min-w-[64px] text-center text-xs font-medium text-zinc-600">
            {safePage} / {safeTotalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(safeTotalPages, safePage + 1))}
            disabled={safePage >= safeTotalPages}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   OrderDetailModal — unchanged (out of scope for list redesign)
───────────────────────────────────────────────────────────────────────────── */

export function OrderDetailModal({ order, loading, onClose, onCancel, onManualAssign }) {
  if (!order && !loading) return null;

  const address = order?.address || {};
  const restaurant = order?.restaurant || {};
  const pricing = order?.pricing || {};
  const finance = financeOf(order || {});
  const driver = driverInfo(order || {});
  const customerPaid = moneyValue(finance.totalFromCustomer, total(order || {}));
  const restaurantGets = moneyValue(finance.restaurantAmount, order?.restaurantAmount, order?.restaurant_amount);
  const adminCut = moneyValue(finance.adminAmount, finance.adminCommissionAmount, order?.adminAmount, order?.admin_amount);
  const driverAmount = moneyValue(finance.driverAmount, order?.driverAmount, order?.driver_amount);
  const taxDeliveryPackaging =
    moneyValue(finance.taxAmount, order?.taxAmount, order?.tax_amount) +
    moneyValue(finance.deliveryFee, order?.deliveryFee, order?.delivery_fee) +
    moneyValue(finance.packagingCharge, order?.packagingCharge, order?.packaging_charge);
  const cancelledOrRefunded = isCancelledOrRefunded(order || {});
  const cancellation = cancellationOf(order || {});
  const issue = issueOf(order || {});
  const refund = refundOf(order || {});
  const hasRefund = hasRefundInfo(order || {});
  const issueReason = cancelReasonOf(order || {});
  const issueSource = issueSourceOf(order || {});
  const issueAt = first(
    issue.cancelledAt, issue.cancelled_at, issue.failedAt, issue.failed_at,
    cancellation.cancelledAt, cancellation.cancelled_at, order?.updatedAt, order?.updated_at
  );
  const refundId = hasRefund ? first(refund.id, refund.refundId, refund.refund_id, refund.raw?.id, "-") : "No refund";
  const paymentId = first(refund.paymentId, refund.payment_id, refund.raw?.payment_id, order?.razorpayPaymentId, order?.razorpay_payment_id, "-");
  const refundStatus = hasRefund ? first(refund.status, refund.raw?.status, "processed") : "NO_REFUND";
  const rollback = cancellation.stockRollback || cancellation.stock_rollback || {};
  const dispatchCancel = cancellation.dispatchCancel || cancellation.dispatch_cancel || {};
  const pickupOtp = otpValue(order || {}, "pickup");
  const deliveryOtp = otpValue(order || {}, "delivery");
  const pickupVerifiedAt = otpVerifiedAt(order || {}, "pickup");
  const deliveryVerifiedAt = otpVerifiedAt(order || {}, "delivery");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[26px] bg-white shadow-2xl ring-1 ring-[#f2c6cc]">
        <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-[#f2c6cc] bg-white p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#fff6f7] px-3 py-1 text-[11px] font-black uppercase tracking-wide text-[#d86a78]">
              <ClipboardList size={13} /> Order detail
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-semibold text-black">{loading ? "Loading order..." : orderNumber(order)}</h2>
              {!loading ? <StatusPill value={order?.status} /> : null}
              {!loading ? <StatusPill value={first(order?.paymentStatus, order?.payment_status)} /> : null}
            </div>
            {!loading ? (
              <div className="mt-1 text-xs font-semibold text-slate-400">
                Created: {timeText(first(order?.createdAt, order?.created_at))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {!loading && onManualAssign && canManualAssignFoodOrder(order) ? (
              <Button variant="orange" onClick={() => onManualAssign?.(order)}>
                <Truck size={16} /> Manual assign
              </Button>
            ) : null}
            {!loading && onCancel && canCancelFoodOrder(order) ? (
              <Button variant="ghost" onClick={() => onCancel?.(order)}>
                <X size={16} /> Cancel + Refund
              </Button>
            ) : null}
            <Button variant="ghost" onClick={onClose}>
              <X size={16} /> Close
            </Button>
          </div>
        </div>

        <div className="max-h-[calc(92vh-120px)] overflow-y-auto p-5">
          {loading ? <LoadingBox label="Loading food order detail..." /> : null}
          {!loading && order ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <MoneyTile label="Customer paid" value={customerPaid} />
                <MoneyTile label="Restaurant gets" value={restaurantGets} />
                <MoneyTile label="Admin cut" value={adminCut} />
                <MoneyTile label="Driver amount" value={driverAmount} />
              </div>

              <DetailBlock title="OTP Verification" icon={<KeyRound size={15} />}>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <OtpTile
                    label="Restaurant pickup OTP"
                    value={pickupOtp}
                    verified={otpVerified(order, "pickup")}
                    verifiedAt={pickupVerifiedAt}
                    helper="Restaurant gives this OTP to driver at handover. Driver pickup API rejects wrong OTP."
                  />
                  <OtpTile
                    label="Customer delivery OTP"
                    value={deliveryOtp}
                    verified={otpVerified(order, "delivery")}
                    verifiedAt={deliveryVerifiedAt}
                    helper="Customer gives this OTP to driver at doorstep. Driver delivered API rejects wrong OTP."
                  />
                </div>
              </DetailBlock>

              {cancelledOrRefunded ? (
                <DetailBlock
                  title={hasRefund ? "Refund & cancellation" : "Failure / cancellation"}
                  icon={<AlertTriangle size={15} />}
                >
                  <div className="mb-4 rounded-[16px] border border-rose-200 bg-rose-50 p-4">
                    <div className="text-[10px] font-black uppercase tracking-wide text-rose-500">Reason</div>
                    <div className="mt-1 text-sm font-semibold text-rose-800">{issueReason}</div>
                    <div className="mt-2 text-[11px] font-bold text-rose-500">Source: {issueSource}</div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                      <div className="text-[12px] font-bold text-slate-800">Payment state</div>
                      <div className="mt-1 text-[16px] font-black text-[#d86a78]">
                        {String(first(order.paymentStatus, order.payment_status, "-")).toUpperCase()}
                      </div>
                      <div className="mt-1 text-xs font-bold text-slate-800">
                        {hasRefund ? "Refund was initiated against captured payment" : "No refund needed because payment was not captured"}
                      </div>
                    </div>
                    <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                      <div className="text-[12px] font-bold text-slate-800">Refund amount</div>
                      <div className="mt-1 text-lg font-semibold text-slate-900/60">
                        {hasRefund ? formatCurrency(refundAmountRupees(refund, total(order))) : "-"}
                      </div>
                      <div className="mt-1 text-xs font-semibold text-slate-500">
                        {hasRefund ? "Razorpay refund" : "Payment failed/cancelled"}
                      </div>
                    </div>
                    <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                      <div className="text-[12px] font-bold text-slate-800">Refund ID</div>
                      <div className="mt-1 break-all text-sm font-semibold text-slate-900/60">{refundId}</div>
                      <div className="mt-1 break-all text-xs font-semibold text-slate-500">Payment: {paymentId}</div>
                    </div>
                    <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                      <div className="text-[12px] font-bold text-slate-800">Issue time</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900/60">{timeText(issueAt)}</div>
                      <div className="mt-1 text-xs font-semibold text-slate-500">
                        Refund status: {String(refundStatus || "-").toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                      <div className="text-[12px] font-bold text-slate-800">Food lock rollback</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900/60">
                        {rollback.done === false ? "Not completed" : "Released / not required"}
                      </div>
                      <div className="mt-1 text-xs font-semibold text-slate-500">
                        Food orders do not use warehouse batch stock
                      </div>
                    </div>
                    <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                      <div className="text-[12px] font-bold text-slate-800">Driver reservation</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900/60">
                        {dispatchCancel.cancelled === false ? "Not cancelled" : "Cancelled / released"}
                      </div>
                      <div className="mt-1 text-xs font-semibold text-slate-500">
                        Task status: {first(dispatchCancel.task?.task_status, dispatchCancel.task?.taskStatus, driver.taskStatus, "CANCELLED")}
                      </div>
                    </div>
                  </div>
                </DetailBlock>
              ) : null}

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
                <DetailBlock title="Customer" icon={<UserRound size={15} />}>
                  <div className="text-lg font-semibold text-black">
                    {first(order.customerName, order.customer_name, address.name, "-")}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-500">
                    <Phone size={14} />
                    {first(order.customerPhone, order.customer_phone, address.phone, address.mobile, "-")}
                  </div>
                  <div className="mt-3 rounded-[14px] bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">
                    {addressText(address, order.delivery_address)}
                  </div>
                </DetailBlock>

                <DetailBlock title="Restaurant snapshot" icon={<Store size={15} />}>
                  <div className="text-lg font-semibold text-black">
                    {first(restaurant.name, order.restaurantName, order.restaurant_name, "-")}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-500">
                    Phone: {first(restaurant.phone, restaurant.mobile, "-")}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-500">
                    Commission:{" "}
                    {first(finance.commissionPercent, restaurant.adminCommissionPercent, restaurant.admin_commission_percent, 0)}%
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-[12px] border border-[#f2c6cc] p-3 font-semibold text-emerald-700">
                      Gets
                      <br />
                      {formatCurrency(restaurantGets)}
                    </div>
                    <div className="rounded-[12px] border border-[#f2c6cc] p-3 font-semibold text-orange-700">
                      Admin
                      <br />
                      {formatCurrency(adminCut)}
                    </div>
                  </div>
                </DetailBlock>

                <DetailBlock title="Driver snapshot" icon={<Truck size={15} />}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="break-words text-lg font-semibold text-black">{driver.displayName}</div>
                      <div className="mt-1 break-words text-sm font-semibold text-slate-500">
                        Phone: {driver.phone || "-"}
                      </div>
                    </div>
                    <div className="max-w-[55%] shrink-0">
                      <StatusPill value={driver.status} />
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-semibold text-slate-600">
                    <div className="min-w-0 rounded-[12px] border border-[#f2c6cc] p-3">
                      Assignment
                      <br />
                      <b className="break-all">{driver.assignment}</b>
                    </div>
                    <div className="min-w-0 rounded-[12px] border border-[#f2c6cc] p-3">
                      Auto assigned
                      <br />
                      <b className="break-all">{driver.autoAssigned ? "Yes" : "No"}</b>
                    </div>
                  </div>
                  {cancelledOrRefunded ? (
                    <div className="mt-3 rounded-[12px] border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-800">
                      Delivery reservation/task has been cancelled or released because this food order failed/cancelled.
                    </div>
                  ) : null}
                  {driver.lastNote ? (
                    <div className="mt-3 break-words rounded-[12px] border border-[#f2c6cc] p-3 text-xs font-semibold text-slate-600">
                      {driver.lastNote}
                    </div>
                  ) : null}
                </DetailBlock>
              </div>

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.35fr_.65fr]">
                <DetailBlock title="Order Items" icon={<ClipboardList size={15} />}>
                  <div className="space-y-3">
                    {itemsOf(order).map((item, idx) => {
                      const parent = parentItemName(item);
                      const name = itemName(item);
                      return (
                        <div
                          key={item.id || idx}
                          className="flex flex-col gap-3 rounded-[16px] bg-slate-50 p-4 md:flex-row md:items-center md:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="text-base font-semibold text-black">{name}</div>
                            {parent && parent !== name ? (
                              <div className="mt-0.5 text-xs font-semibold text-slate-400">Parent: {parent}</div>
                            ) : null}
                            {first(item.variantName, item.variant_name) ? (
                              <div className="mt-0.5 text-xs font-bold text-[#d86a78]">
                                Variant: {first(item.variantName, item.variant_name)}
                              </div>
                            ) : null}
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              Qty {itemQty(item)} × {formatCurrency(itemUnitPrice(item))}
                            </div>
                          </div>
                          <div className="text-right text-lg font-semibold text-black">
                            {formatCurrency(itemLineTotal(item))}
                          </div>
                        </div>
                      );
                    })}
                    {!itemsOf(order).length ? (
                      <EmptyState title="No items found" subtitle="This order has no item snapshot." />
                    ) : null}
                  </div>
                </DetailBlock>

                <DetailBlock title="Money split" icon={<BadgeIndianRupee size={15} />}>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between rounded-[12px] bg-slate-50 px-3 py-2 font-bold text-slate-800">
                      <span>Subtotal</span>
                      <span>{formatCurrency(first(order.subtotal, pricing.subtotal, 0))}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-[12px] px-3 py-2 font-semibold text-slate-800">
                      <span>Restaurant payout</span>
                      <span>{formatCurrency(restaurantGets)}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-[12px] px-3 py-2 font-semibold text-slate-800">
                      <span>Admin commission</span>
                      <span>{formatCurrency(adminCut)}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-[12px] px-3 py-2 font-semibold text-slate-800">
                      <span>Driver amount</span>
                      <span>{formatCurrency(driverAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-[12px] px-3 py-2 font-semibold text-slate-800">
                      <span>Tax + delivery + packaging</span>
                      <span>{formatCurrency(taxDeliveryPackaging)}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-[12px] bg-[#d86a78] px-3 py-3 font-black text-white">
                      <span>Grand total</span>
                      <span>{formatCurrency(total(order))}</span>
                    </div>
                  </div>
                </DetailBlock>
              </div>

              <DetailBlock title="Timeline" icon={<CalendarClock size={15} />}>
                <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-5">
                  <div className="rounded-[14px] bg-slate-50 p-3">
                    <span className="text-xs font-bold text-slate-400">Received</span>
                    <div className="font-semibold text-slate-800">{timeText(driver.receivedAt)}</div>
                  </div>
                  <div className="rounded-[14px] bg-slate-50 p-3">
                    <span className="text-xs font-bold text-slate-400">Picked</span>
                    <div className="font-semibold text-slate-800">{timeText(driver.pickedUpAt)}</div>
                  </div>
                  <div className="rounded-[14px] bg-slate-50 p-3">
                    <span className="text-xs font-bold text-slate-400">Out for delivery</span>
                    <div className="font-semibold text-slate-800">{timeText(driver.outForDeliveryAt)}</div>
                  </div>
                  <div className="rounded-[14px] bg-slate-50 p-3">
                    <span className="text-xs font-bold text-slate-400">Delivered</span>
                    <div className="font-semibold text-slate-800">{timeText(driver.deliveredAt)}</div>
                  </div>
                  <div className="rounded-[14px] bg-rose-50 p-3">
                    <span className="text-xs font-bold text-rose-400">Cancelled / refunded</span>
                    <div className="font-semibold text-rose-700">
                      {cancelledOrRefunded ? timeText(issueAt) : "-"}
                    </div>
                  </div>
                </div>
                {needsManualAssignment(order) ? (
                  <div className="mt-4 rounded-[16px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <div className="font-black">Manual assignment required</div>
                    <div className="mt-1">Auto assign failed because no eligible rider matched all rules.</div>
                    {onManualAssign && canManualAssignFoodOrder(order) ? (
                      <Button size="sm" variant="orange" className="mt-3" onClick={() => onManualAssign?.(order)}>
                        <Truck size={14} /> Open manual assignment
                      </Button>
                    ) : null}
                    {assignmentDiagnostics(order)?.rejected?.length ? (
                      <div className="mt-3 grid gap-2 md:grid-cols-3">
                        {assignmentDiagnostics(order).rejected.slice(0, 3).map((r, idx) => (
                          <div key={idx} className="rounded-[12px] bg-white p-3">
                            <div className="font-bold">{r.name || "Rider"}</div>
                            <div className="text-xs">
                              {Array.isArray(r.reasons) ? r.reasons.join(", ") : "Not assignable"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </DetailBlock>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}


function ManualAssignModal({ order, options, loading, error, assigningPartnerId, onRefresh, onAssign, onClose }) {
  if (!order) return null;
  const assignable = Array.isArray(options?.assignablePartners) ? options.assignablePartners : [];
  const unavailable = Array.isArray(options?.unavailablePartners) ? options.unavailablePartners : [];
  const hasAssignable = assignable.length > 0;

  const DriverCard = ({ driver, disabled = false }) => {
    const distance = driver?.pickupDistanceKm == null ? "-" : `${driver.pickupDistanceKm} km`;
    const busyText = [
      `${driver?.activeOrderCount ?? 0}/${driver?.maxActiveOrders ?? "-"} active`,
      `${driver?.activeFoodOrderCount ?? 0}/${driver?.maxFoodOrders ?? "-"} food`,
    ].join(" • ");
    const reasons = Array.isArray(driver?.reasons) ? driver.reasons : [];
    const key = driver?.id || driver?.partnerId || driver?.partnerCode || driver?.phone;
    const assigning = assigningPartnerId && String(assigningPartnerId) === String(driver?.id);

    return (
      <div className={`rounded-2xl border p-4 ${disabled ? "border-zinc-200 bg-zinc-50" : "border-emerald-200 bg-emerald-50/40"}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-black text-zinc-900">{driver?.name || "Driver"}</div>
            <div className="mt-1 text-xs font-semibold text-zinc-500">
              {[driver?.phone, driver?.partnerId || driver?.partnerCode, driver?.vehicleNumber].filter(Boolean).join(" • ") || "Driver details unavailable"}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-bold">
              <span className="rounded-full bg-white px-2 py-1 text-zinc-600 ring-1 ring-zinc-200">Pickup {distance}</span>
              <span className="rounded-full bg-white px-2 py-1 text-zinc-600 ring-1 ring-zinc-200">{busyText}</span>
              <span className={`rounded-full px-2 py-1 ring-1 ${driver?.locationFresh ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-amber-200"}`}>
                {driver?.locationFresh ? "Fresh location" : `Location ${driver?.lastSeenAgeText || "not fresh"}`}
              </span>
            </div>
            {reasons.length ? (
              <div className="mt-2 text-xs font-semibold leading-5 text-rose-600">{reasons.join(", ")}</div>
            ) : (
              <div className="mt-2 text-xs font-semibold leading-5 text-emerald-700">Assignable now. Driver will get the food pickup task immediately.</div>
            )}
          </div>
          {!disabled ? (
            <Button size="sm" variant="dark" disabled={assigning} onClick={() => onAssign?.(driver)}>
              <Truck size={14} /> {assigning ? "Assigning…" : "Assign"}
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-zinc-950/55 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-zinc-100 px-6 py-5">
          <div>
            <p className="text-[11px] font-black uppercase tracking-widest text-[#d86a78]">Manual Driver Assignment</p>
            <h3 className="mt-1 text-xl font-black text-zinc-900">{orderNumber(order)}</h3>
            <p className="mt-1 text-sm font-semibold text-zinc-500">
              Assign a driver only when food auto-assignment failed. Auto-assign code is untouched.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="ghost" disabled={loading} onClick={onRefresh}>
              <RefreshCw size={14} /> Refresh
            </Button>
            <button type="button" onClick={onClose} className="rounded-xl p-2 text-zinc-400 transition hover:bg-zinc-100">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(92vh-112px)] overflow-y-auto px-6 py-5">
          {error ? <ErrorBox error={error} onRetry={onRefresh} /> : null}
          {loading ? <LoadingBox label="Loading assignable food drivers…" /> : null}

          {!loading ? (
            <div className="space-y-5">
              <div className={`rounded-2xl border px-4 py-3 ${hasAssignable ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                <div className="font-black">{hasAssignable ? `${assignable.length} driver${assignable.length > 1 ? "s" : ""} assignable now` : "No driver is assignable right now"}</div>
                <div className="mt-1 text-sm font-semibold opacity-80">
                  {hasAssignable ? "Choose one driver below. The driver app will receive a food pickup task." : "Check unavailable drivers below for exact block reason: offline, stale location, active order limit, radius, or food pickup lock."}
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-black uppercase tracking-wide text-zinc-500">Assignable drivers</div>
                <div className="grid gap-3 lg:grid-cols-2">
                  {assignable.map((driver) => <DriverCard key={driver?.id || driver?.partnerId} driver={driver} />)}
                  {!assignable.length ? <EmptyState title="No assignable driver" subtitle="No food rider currently passes all assignment rules." /> : null}
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-black uppercase tracking-wide text-zinc-500">Unavailable approved drivers</div>
                <div className="grid gap-3 lg:grid-cols-2">
                  {unavailable.map((driver) => <DriverCard key={driver?.id || driver?.partnerId} driver={driver} disabled />)}
                  {!unavailable.length ? <EmptyState title="No unavailable driver details" subtitle="Refresh with current driver presence to see rejection reasons." /> : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main page — fully redesigned list screen
───────────────────────────────────────────────────────────────────────────── */

export default function FoodOrdersPage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [manualPopupDismissed, setManualPopupDismissed] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [manualAssignTarget, setManualAssignTarget] = useState(null);
  const [manualAssignOptions, setManualAssignOptions] = useState(null);
  const [manualAssignLoading, setManualAssignLoading] = useState(false);
  const [manualAssignError, setManualAssignError] = useState("");
  const [assigningPartnerId, setAssigningPartnerId] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await restaurantAdminService.listFoodOrders({ status, q, limit: 100 });
      setRows(rowsOf(res));
      setPage(1);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  async function openDetail(orderId) {
    if (!orderId) return;
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await restaurantAdminService.getFoodOrder(orderId);
      setDetail(unwrap(res));
    } catch (e) {
      setError(e);
    } finally {
      setDetailLoading(false);
    }
  }

  async function loadManualAssignOptions(orderArg = manualAssignTarget) {
    const orderId = idOf(orderArg);
    if (!orderId) return;
    setManualAssignLoading(true);
    setManualAssignError("");
    try {
      const res = await restaurantAdminService.getFoodOrderManualAssignmentOptions(orderId, { includeUnavailable: true });
      setManualAssignOptions(unwrap(res));
    } catch (e) {
      setManualAssignError(e);
    } finally {
      setManualAssignLoading(false);
    }
  }

  async function openManualAssign(order) {
    if (!order) return;
    setManualAssignTarget(order);
    setManualAssignOptions(null);
    setManualAssignError("");
    await loadManualAssignOptions(order);
  }

  async function submitManualAssign(driver) {
    const orderId = idOf(manualAssignTarget);
    const partnerId = driver?.id || driver?.partnerId || driver?.partnerCode;
    if (!orderId || !partnerId) return;
    setAssigningPartnerId(String(driver?.id || partnerId));
    setManualAssignError("");
    try {
      const res = await restaurantAdminService.manualAssignFoodOrder(orderId, { partnerId });
      const payload = unwrap(res);
      const updated = payload?.order || payload?.data?.order || res?.data?.order || null;
      setManualAssignTarget(null);
      setManualAssignOptions(null);
      if (detailOpen && idOf(detail) === orderId) setDetail(updated || detail);
      await load();
      if (detailOpen && orderId) await openDetail(orderId);
    } catch (e) {
      setManualAssignError(e);
    } finally {
      setAssigningPartnerId("");
    }
  }

  function openCancel(order) {
    if (!order) return;
    setCancelTarget(order);
    setCancelReason("");
    setError("");
  }

  async function submitCancel() {
    const orderId = idOf(cancelTarget);
    const reason = cancelReason.trim();
    if (!orderId || reason.length < 3) {
      setError(new Error("Cancellation reason is required"));
      return;
    }
    setCancelLoading(true);
    setError("");
    try {
      const res = await restaurantAdminService.cancelFoodOrder(orderId, { reason, refundSpeed: "normal" });
      const updated =
        unwrap(res)?.order || unwrap(res)?.data?.order || res?.data?.order || null;
      setCancelTarget(null);
      setCancelReason("");
      if (detailOpen && idOf(detail) === orderId) setDetail(updated || detail);
      await load();
      if (detailOpen && orderId) await openDetail(orderId);
    } catch (e) {
      setError(e);
    } finally {
      setCancelLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  const summary = useMemo(
    () => ({
      total: rows.length,
      paid: rows.filter((r) => String(first(r.paymentStatus, r.payment_status)).toUpperCase() === "PAID").length,
      assigned: rows.filter((r) => !["", "NOT_ASSIGNED"].includes(String(driverStatus(r) || "").toUpperCase())).length,
      revenue: rows.reduce((sum, r) => {
        const orderStatus = String(first(r.status, r.orderStatus, r.order_status, "")).toUpperCase();
        const paymentStatus = String(first(r.paymentStatus, r.payment_status, "")).toUpperCase();
        if (["CANCELLED", "FAILED", "REJECTED"].includes(orderStatus)) return sum;
        if (["REFUNDED", "FAILED"].includes(paymentStatus)) return sum;
        return sum + Number(total(r) || 0);
      }, 0),
    }),
    [rows]
  );

  const manualOrders = useMemo(() => rows.filter(needsManualAssignment), [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedRows = rows.slice(startIndex, startIndex + pageSize);

  if (loading) return <LoadingBox label="Loading food orders…" />;

  /* ── Column definitions ──────────────────────────────────────────────────── */
  const COLS = [
    { label: "Order",       w: "w-[130px]" },
    { label: "Restaurant",  w: "w-[150px]" },
    { label: "Customer",    w: "w-[160px]" },
    { label: "Items",       w: "w-[70px]"  },
    { label: "OTP",         w: "w-[110px]" },
    { label: "Amount",      w: "w-[100px]" },
    { label: "Payment",     w: "w-[120px]" },
    { label: "Order status",w: "w-[160px]" },
    { label: "Reason",      w: "w-[200px]" },
    { label: "Driver",      w: "w-[160px]" },
    { label: "Action",      w: "w-[190px]" },
  ];

  return (
    <div className="space-y-5">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          {/* <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-400">Orders</p> */}
          <h1 className="mt-0.5 text-2xl font-bold text-[#d8687b] text-zinc-900">Orders</h1>
          <p className="mt-1 text-sm text-zinc-500">
            All restaurant orders — customer, restaurant, driver and financials open after View.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-600 shadow-sm transition hover:bg-zinc-50 active:scale-[0.98]"
        >
          <RefreshCw size={14} className="text-zinc-400" />
          Refresh
        </button>
      </div>

      <ErrorBox error={error} onRetry={load} />

      {/* ── Metrics strip ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: "Total orders",    value: summary.total,              fmt: (v) => v },
          { label: "Paid",            value: summary.paid,               fmt: (v) => v },
          { label: "Driver assigned", value: summary.assigned,           fmt: (v) => v },
          { label: "Revenue",         value: summary.revenue,            fmt: (v) => formatCurrency(v), accent: true },
        ].map(({ label, value, fmt, accent }) => (
          <div
            key={label}
            className={`rounded-xl border px-4 py-3 ${accent ? "border-amber-200 bg-amber-50" : "border-zinc-200 bg-white"}`}
          >
            <p className={`text-[11px] font-medium uppercase tracking-wide ${accent ? "text-amber-600" : "text-zinc-400"}`}>
              {label}
            </p>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${accent ? "text-amber-900" : "text-zinc-900"}`}>
              {fmt(value)}
            </p>
          </div>
        ))}
      </div>

      {/* ── Manual assignment alert ───────────────────────────────────────── */}
      {manualOrders.length > 0 && !manualPopupDismissed ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {manualOrders.length} order{manualOrders.length > 1 ? "s" : ""} need manual rider assignment
                </p>
                <p className="mt-0.5 text-xs text-amber-700">
                  Auto-assign couldn't find an eligible rider — check driver status, radius, and active order limits.
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {manualOrders.slice(0, 5).map((o) => (
                    <button
                      key={idOf(o)}
                      type="button"
                      onClick={() => openManualAssign(o)}
                      className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 transition hover:bg-amber-50"
                    >
                      {orderNumber(o)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setManualPopupDismissed(true)}
              className="mt-0.5 shrink-0 rounded-md p-1 text-amber-500 transition hover:bg-amber-100"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Search / filter bar ───────────────────────────────────────────── */}
      <div className="flex flex-col gap-2.5 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm md:flex-row md:items-end">
        <div className="flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-400">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Order ID, customer name, restaurant…"
              className="h-9 w-full rounded-lg border border-zinc-200 bg-zinc-50 pl-9 pr-3 text-sm text-zinc-800 outline-none placeholder:text-zinc-400 focus:border-zinc-400 focus:bg-white focus:ring-2 focus:ring-zinc-100 transition"
            />
          </div>
        </div>
        <div className="w-full md:w-52">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-400">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 text-sm text-zinc-800 outline-none focus:border-zinc-400 focus:bg-white focus:ring-2 focus:ring-zinc-100 transition"
          >
            <option value="">All statuses</option>
            <option value="PAYMENT_PENDING">Payment pending</option>
            <option value="AUTO_ACCEPTED">Auto accepted</option>
            <option value="DELIVERY_ASSIGNED">Delivery assigned</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="PICKUP_PENDING">Pickup pending</option>
            <option value="PICKED_UP">Picked up</option>
            <option value="OUT_FOR_DELIVERY">Out for delivery</option>
            <option value="DELIVERED">Delivered</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <button
          type="button"
          onClick={load}
          className="h-9 shrink-0 rounded-lg bg-zinc-900 px-5 text-sm font-medium text-white transition hover:bg-zinc-700 active:scale-[0.98]"
        >
          Apply
        </button>
      </div>

      {/* ── Orders table ──────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        {rows.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1520px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    {COLS.map(({ label, w }) => (
                      <th
                        key={label}
                        className={`${w} px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-400`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r) => {
                    const rid = idOf(r);
                    const driver = driverInfo(r);
                    const itemCount = first(r.itemCount, r.item_count, Array.isArray(r.items) ? r.items.length : 0);
                    const reason = cancelReasonOf(r);
                    const pickupDone = otpVerified(r, "pickup");
                    const deliveryDone = otpVerified(r, "delivery");
                    const pickupCode = otpValue(r, "pickup");
                    const deliveryCode = otpValue(r, "delivery");

                    return (
                      <tr
                        key={rid}
                        onClick={() => openDetail(rid)}
                        className="group cursor-pointer border-b border-zinc-100 transition-colors last:border-0 hover:bg-zinc-50"
                      >
                        {/* Order */}
                        <td className="px-4 py-3.5 align-middle">
                          <p className="font-mono text-[13px] font-bold text-zinc-900 group-hover:text-zinc-600 transition-colors">
                            {orderNumber(r)}
                          </p>
                          <p className="mt-0.5 text-[11px] text-zinc-400">
                            {dateText(first(r.createdAt, r.created_at))}
                          </p>
                        </td>

                        {/* Restaurant */}
                        <td className="px-4 py-3.5 align-middle">
                          <p className="truncate text-[13px] font-semibold text-zinc-800 max-w-[140px]">
                            {first(r.restaurantName, r.restaurant_name, r.restaurant?.name, "—")}
                          </p>
                        </td>

                        {/* Customer */}
                        <td className="px-4 py-3.5 align-middle">
                          <p className="text-[13px] font-semibold text-zinc-800">
                            {first(r.customerName, r.customer_name, r.address?.name, "—")}
                          </p>
                          <p className="mt-0.5 text-[11px] text-zinc-400">
                            {first(r.customerPhone, r.customer_phone, r.address?.phone, "")}
                          </p>
                        </td>

                        {/* Items */}
                        <td className="px-4 py-3.5 align-middle">
                          <span className="inline-flex h-6 min-w-[28px] items-center justify-center rounded-md bg-zinc-100 px-1.5 text-xs font-semibold text-zinc-600">
                            {itemCount}
                          </span>
                        </td>

                        {/* OTP */}
                        <td className="px-4 py-3.5 align-middle">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 w-3">P</span>
                              {pickupDone ? (
                                <span className="text-[11px] font-semibold text-emerald-600">✓ Verified</span>
                              ) : (
                                <span className="font-mono text-xs font-bold tracking-widest text-zinc-700">
                                  {pickupCode || "—"}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 w-3">D</span>
                              {deliveryDone ? (
                                <span className="text-[11px] font-semibold text-emerald-600">✓ Verified</span>
                              ) : (
                                <span className="font-mono text-xs font-bold tracking-widest text-zinc-700">
                                  {deliveryCode || "—"}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3.5 align-middle">
                          <p className="text-[13px] font-bold text-zinc-900 tabular-nums">
                            {formatCurrency(total(r))}
                          </p>
                        </td>

                        {/* Payment status */}
                        <td className="px-4 py-3.5 align-middle">
                          <Badge value={first(r.paymentStatus, r.payment_status)} />
                        </td>

                        {/* Order status */}
                        <td className="px-4 py-3.5 align-middle">
                          <Badge value={r.status} />
                        </td>

                        {/* Cancel reason */}
                        <td className="px-4 py-3.5 align-middle">
                          {reason !== "-" ? (
                            <p
                              title={reason}
                              className="max-w-[188px] truncate rounded-md bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-rose-100"
                            >
                              {reason}
                            </p>
                          ) : (
                            <span className="text-zinc-300 text-xs">—</span>
                          )}
                        </td>

                        {/* Driver */}
                        <td className="px-4 py-3.5 align-middle">
                          <p className="text-[13px] font-semibold text-zinc-800">{driver.displayName}</p>
                          <div className="mt-0.5">
                            <Badge value={driver.status} />
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5 align-middle">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openDetail(rid); }}
                              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-600 transition hover:bg-zinc-50 hover:border-zinc-300"
                            >
                              <Eye size={12} />
                              View
                            </button>
                            {canManualAssignFoodOrder(r) ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openManualAssign(r); }}
                                className="inline-flex h-7 items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 text-xs font-medium text-amber-700 transition hover:bg-amber-100"
                              >
                                <Truck size={12} />
                                Assign
                              </button>
                            ) : null}
                            {canCancelFoodOrder(r) ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openCancel(r); }}
                                className="inline-flex h-7 items-center gap-1 rounded-md border border-rose-200 bg-white px-2.5 text-xs font-medium text-rose-600 transition hover:bg-rose-50"
                              >
                                <X size={12} />
                                Cancel
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={safePage}
              totalPages={totalPages}
              totalItems={rows.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              itemLabel="orders"
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 px-5 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
              <ClipboardList size={22} className="text-zinc-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-zinc-700">No food orders</p>
              <p className="mt-0.5 text-xs text-zinc-400">Orders will appear here after restaurant checkout.</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail modal ──────────────────────────────────────────────────── */}
      {detailOpen ? (
        <OrderDetailModal
          order={detail}
          loading={detailLoading}
          onCancel={openCancel}
          onManualAssign={openManualAssign}
          onClose={() => { setDetailOpen(false); setDetail(null); }}
        />
      ) : null}

      {manualAssignTarget ? (
        <ManualAssignModal
          order={manualAssignTarget}
          options={manualAssignOptions}
          loading={manualAssignLoading}
          error={manualAssignError}
          assigningPartnerId={assigningPartnerId}
          onRefresh={() => loadManualAssignOptions(manualAssignTarget)}
          onAssign={submitManualAssign}
          onClose={() => { setManualAssignTarget(null); setManualAssignOptions(null); setManualAssignError(""); }}
        />
      ) : null}

      {/* ── Cancel modal ──────────────────────────────────────────────────── */}
      {cancelTarget ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white shadow-2xl">
            <div className="border-b border-zinc-100 px-6 pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-500">
                    Cancel + Razorpay refund
                  </p>
                  <h3 className="mt-1 text-lg font-bold text-zinc-900">
                    Order {orderNumber(cancelTarget)}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setCancelTarget(null)}
                  className="rounded-lg p-1.5 text-zinc-400 transition hover:bg-zinc-100"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="mt-2 text-sm text-zinc-500">
                This will cancel the food order, release driver reservation, mark food locks cancelled, and initiate a Razorpay refund.
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Cancellation reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                placeholder="e.g. Restaurant cannot prepare this order / item unavailable"
                className="mt-2 w-full resize-none rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-800 outline-none placeholder:text-zinc-400 focus:border-zinc-400 focus:bg-white focus:ring-2 focus:ring-zinc-100 transition"
              />
              <p className="mt-1.5 text-[11px] text-zinc-400">Minimum 3 characters required.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-zinc-100 px-6 py-4">
              <button
                type="button"
                disabled={cancelLoading}
                onClick={() => setCancelTarget(null)}
                className="h-9 rounded-lg border border-zinc-200 px-4 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 disabled:opacity-50"
              >
                Keep order
              </button>
              <button
                type="button"
                disabled={cancelLoading || cancelReason.trim().length < 3}
                onClick={submitCancel}
                className="h-9 rounded-lg bg-rose-600 px-4 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
              >
                {cancelLoading ? "Cancelling…" : "Cancel order + refund"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}